import { DatePipe } from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { NgModule, Injector, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
  MissingTranslationHandler,
} from '@ngx-translate/core';
import { StoreModule } from '@ngrx/store';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideNgToast, TOAST_POSITIONS } from 'ng-angular-popup';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  QueryClient,
  provideTanStackQuery,
} from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';

import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { StaticModule } from './static/static.module';
import { AppRoutingModule } from './app-routing.module';
import { HelpModule } from '@help/help.module';

import {
  CustomTranslateHttpLoader,
  MissingTranslationService,
} from './shared/missing.translation.service';
import { CookieService } from 'ngx-cookie-service';
import { StoreService } from '@services/store.service';

import { AppComponent } from './app.component';

import { environment } from '@env/environment';
import { appInitFactory } from '@factories/app-init.factory';
import { authInitFactory } from '@factories/auth-init.factory';
import { Store } from '@ngrx/store';
import { AuthService } from './core/services/auth.service';

import { EffectsModule } from '@ngrx/effects';
import { AuthEffects } from './core/effects/auth.effects';
import { CoreEffects } from './core/effects/core.effects';
import { FeedEffects } from './core/effects/feed.effects';
import { PostEffects } from './core/effects/post.effects';
import { ObjectEffects } from './core/effects/object.effects';
import { WidgetEffects } from './core/effects/widget.effects';
import { PermissionsEffects } from './core/effects/permissions.effects';
import { OutputSettingsEffects } from './core/effects/output-settings.effects';

import { reducers } from './reducers/index';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateHttpLoader,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MissingTranslationService,
      },
    }),
    AngularSvgIconModule.forRoot(),
    StaticModule,
    FontAwesomeModule,
    CoreModule,
    AppRoutingModule, // !!! Moved after CoreModule to ensure CoreModule routes are processed first
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([
      AuthEffects,
      CoreEffects,
      FeedEffects,
      WidgetEffects,
      PostEffects,
      OutputSettingsEffects,
      PermissionsEffects,
      ObjectEffects,
    ]),
    SharedModule,
    HelpModule,
    !environment.production
      ? StoreDevtoolsModule.instrument({
          name: ':: Sensika ::',
          maxAge: 25,
          // * logOnly: true  -> restricts DevTools to read-only mode (recommended for Production)
          // * logOnly: false -> enables full features: action replay, time-travel, manual dispatching (good for Dev)
          // * logOnly = false -> full debug tools enabled
          // * logOnly = true -> no State mutation allowed from DevTools!
          logOnly: !environment.production,
          serialize: {
            options: {
              map: true,
            },
          },
          connectInZone: true,
        })
      : [],
  ],
  providers: [
    CookieService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitFactory,
      deps: [TranslateService, Injector],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: authInitFactory,
      deps: [AuthService, Store],
      multi: true,
    },
    StoreService,
    DatePipe,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideNgToast({
      duration: 3000,
      position: TOAST_POSITIONS.TOP_CENTER,
      showIcon: true,
    }),
    environment.production
      ? provideTanStackQuery(new QueryClient())
      : provideTanStackQuery(
          new QueryClient(),
          withDevtools(() => ({
            loadDevtools:
              localStorage.getItem('queryDevtools') === 'true' ? 'auto' : false,
          })),
        ),
  ],
})
export class AppModule {}
